import { useState, useCallback, useEffect } from 'react';

/**
 * Persists nested-tab selection state to sessionStorage so that browser Back
 * navigation can restore which entity was expanded and which sub-tab was active.
 *
 * Storage keys: `${pageKey}:${entityId}:selectedId` and `${pageKey}:${entityId}:nested`
 *
 * @example
 * const { selectedId, setSelectedId, nested, setNested, clear } = useNestedSelection({
 *   pageKey: 'clientGroup',
 *   entityId: id,
 *   defaultNested: 'overview',
 * });
 */
export function useNestedSelection<T extends string = string>({
  pageKey,
  entityId,
  defaultNested = 'overview' as T,
}: {
  pageKey: string;
  entityId: string | undefined;
  defaultNested?: T;
}): {
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  nested: T;
  setNested: (value: T) => void;
  clear: () => void;
} {
  const storageKey = (field: string) =>
    entityId ? `${pageKey}:${entityId}:${field}` : null;

  const [selectedId, setSelectedIdState] = useState<string | null>(() => {
    const key = storageKey('selectedId');
    if (!key) return null;
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  });

  const [nested, setNestedState] = useState<T>(() => {
    const key = storageKey('nested');
    if (!key) return defaultNested;
    try {
      return (sessionStorage.getItem(key) as T) || defaultNested;
    } catch {
      return defaultNested;
    }
  });

  const setSelectedId = useCallback(
    (id: string | null) => {
      setSelectedIdState(id);
      const key = storageKey('selectedId');
      if (!key) return;
      try {
        if (id === null) {
          sessionStorage.removeItem(key);
        } else {
          sessionStorage.setItem(key, id);
        }
      } catch {
        // sessionStorage unavailable (privacy mode) — state-only fallback
      }
    },
[pageKey, entityId],
  );

  const setNested = useCallback(
    (value: T) => {
      setNestedState(value);
      const key = storageKey('nested');
      if (!key) return;
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // sessionStorage unavailable — state-only fallback
      }
    },
[pageKey, entityId],
  );

  const clear = useCallback(() => {
    setSelectedIdState(null);
    setNestedState(defaultNested);
    const selKey = storageKey('selectedId');
    const nestKey = storageKey('nested');
    try {
      if (selKey) sessionStorage.removeItem(selKey);
      if (nestKey) sessionStorage.removeItem(nestKey);
    } catch {
      // sessionStorage unavailable
    }
  }, [pageKey, entityId, defaultNested]);

  // Re-read from sessionStorage if entityId changes (e.g. navigating between groups)
  useEffect(() => {
    const selKey = storageKey('selectedId');
    const nestKey = storageKey('nested');
    try {
      setSelectedIdState(selKey ? sessionStorage.getItem(selKey) : null);
      setNestedState(
        nestKey ? ((sessionStorage.getItem(nestKey) as T) || defaultNested) : defaultNested,
      );
    } catch {
      setSelectedIdState(null);
      setNestedState(defaultNested);
    }
  }, [entityId]);

  return { selectedId, setSelectedId, nested, setNested, clear };
}
